<script lang="ts">
    import {
        VpArrowRightIcon,
        VpCalendarDateIcon,
        VpChevronDownIcon,
        VpInformationIcon,
    } from '@decathlon-experience/icons/svelte';
    import { createAccordion } from '@melt-ui/svelte';
    import dayjs, { type Dayjs } from 'dayjs';
    import { slide } from 'svelte/transition';

    import { trans } from '$assets/shop-translator.js';
    import cn from '$common/utils/cn';
    import { formatTourDate, formatTourDate2 } from '$saga/utils/date';
    import { formatCurrency } from '$saga/utils/price';
    import {
        selectedPrice,
        snowtrexSelectedDuration,
        snowtrexSelectedPax,
    } from '$saga/views/Tour/store';
    import type {
        SnowtrexAccommodation,
        SnowtrexAccommodationDate,
        SnowtrexAccommodationPrice,
        SnowtrexMonthlyPrices,
        SnowtrexSelection,
    } from '$saga/views/Tour/VisibleDates/types';

    import SnowtrexHeaderFilters from './SnowtrexHeaderFilters.svelte';
    import { getCheapestSnowtrexPrice, getSnowtrexSelection } from './utils';

    export let snowtrexAccommodationDates: SnowtrexAccommodationDate[] = [];
    export let cheapestMonthlyPrices: SnowtrexMonthlyPrices = {};
    export let onAccommodationSelected: (selection: SnowtrexSelection) => void;

    /**
     * Resolve the accommodation's price for the selected participant count.
     */
    const getAccommodationPrice = (
        accommodation: SnowtrexAccommodation,
        pax: number,
    ): SnowtrexAccommodationPrice => accommodation.pricesByPax[pax]!;

    /**
     * Select an accommodation and open the booking drawer.
     */
    const selectAccommodation = (
        period: SnowtrexAccommodationDate,
        accommodation: SnowtrexAccommodation,
    ) => {
        const selection = getSnowtrexSelection(
            period,
            accommodation,
            $snowtrexSelectedPax,
        );
        $selectedPrice = {
            originalPrice: selection.selectedPrice.originalPrice,
            actualPrice: selection.selectedPrice.price,
            rebateAmount: selection.selectedPrice.rebateAmount,
        };
        onAccommodationSelected(selection);
    };

    /**
     * Get unique durations from the snowtrexAccommodationDates and sort them in ascending order.
     * This will be used to filter the accommodation dates based on the selected duration.
     */
    const durations = Array.from(
        new Set(snowtrexAccommodationDates.map((period) => period.duration)),
    ).sort((left, right) => left - right);

    /**
     * Reactive filter variables
     */
    const availablePax = Array.from(
        new Set(
            snowtrexAccommodationDates
                .flatMap((period) => period.accommodations)
                .flatMap((accommodation) =>
                    Object.keys(accommodation.pricesByPax).map(Number),
                ),
        ),
    ).sort((left, right) => left - right);

    $: if (
        availablePax.length > 0 &&
        !availablePax.includes($snowtrexSelectedPax)
    ) {
        $snowtrexSelectedPax = availablePax[0];
    }

    $: if (
        durations.length > 0 &&
        ($snowtrexSelectedDuration === null ||
            !durations.includes($snowtrexSelectedDuration))
    ) {
        $snowtrexSelectedDuration = durations[0];
    }

    let selectedMonth: Dayjs | null = null;

    const {
        elements: { root, item, trigger, content },
        helpers: { isSelected },
    } = createAccordion({ multiple: true });

    $: if (
        selectedMonth !== null &&
        getCheapestSnowtrexPrice(
            snowtrexAccommodationDates,
            $snowtrexSelectedPax,
            $snowtrexSelectedDuration,
            selectedMonth.format('YYYY-MM'),
        ) === null
    ) {
        selectedMonth = null;
    }

    /**
     * Filtered list of snowtrexAccommodationDates based on the selected duration, pax, and month.
     * This will be used to display the available accommodation dates in the UI.
     */
    $: filteredPeriods = snowtrexAccommodationDates
        .filter(
            (period) =>
                $snowtrexSelectedDuration === null ||
                period.duration === $snowtrexSelectedDuration,
        )
        .filter(
            (period) =>
                selectedMonth === null ||
                dayjs(period.departureDate).isSame(selectedMonth, 'month'),
        )
        .map((period) => ({
            ...period,
            accommodations: period.accommodations
                .filter(
                    (room) =>
                        room.pricesByPax[$snowtrexSelectedPax] !== undefined,
                )
                .sort(
                    (left, right) =>
                        getAccommodationPrice(left, $snowtrexSelectedPax)
                            .price -
                        getAccommodationPrice(right, $snowtrexSelectedPax)
                            .price,
                ),
        }))
        .filter((period) => period.accommodations.length > 0);
</script>

<SnowtrexHeaderFilters
    {durations}
    {availablePax}
    {cheapestMonthlyPrices}
    {snowtrexAccommodationDates}
    bind:selectedDuration={$snowtrexSelectedDuration}
    bind:selectedPax={$snowtrexSelectedPax}
    bind:selectedMonth
/>

{#if filteredPeriods.length > 0}
    <div
        class="mb-xl flex items-center justify-between rounded-lg bg-container-quiet p-s text-content-quiet"
    >
        <div class="text-sm font-semibold">
            {trans('tour.dates.snowtrex.date_departure')}
        </div>
        <div class="mx-auto text-sm font-semibold">
            {trans('tour.dates.snowtrex.accommodation')}
        </div>
        <div class="ml-auto text-sm font-semibold">
            {trans('tour.dates.snowtrex.from')}
        </div>
    </div>
    <div class="">
        <div {...$root} use:root>
            {#each filteredPeriods as period (period.id)}
                {@const cheapest = getAccommodationPrice(
                    period.accommodations[0],
                    $snowtrexSelectedPax,
                )}
                <div
                    {...$item(period.id)}
                    use:item
                    class={cn(
                        'mb-s rounded-lg border border-border-quiet last:mb-0 hover:border-solid hover:border-blue-brand',
                        $isSelected(period.id) &&
                            'border border-solid border-blue-brand bg-commercial-neutral',
                    )}
                >
                    <button
                        type="button"
                        {...$trigger(period.id)}
                        use:trigger
                        class={cn(
                            'flex w-full flex-wrap items-center justify-between gap-m p-s text-left',
                        )}
                    >
                        <span class="flex flex-col">
                            <span class="flex items-center gap-xs">
                                <VpCalendarDateIcon class="size-xl" />
                                <span class="text-sm font-semibold">
                                    {formatTourDate(period.departureDate)}
                                </span>
                            </span>
                            <span class="text-xs text-content-quiet">
                                {trans('tour.dates.to')}
                                {formatTourDate2(period.returnDate)}
                            </span>
                        </span>

                        <span
                            class="flex items-center text-sm text-content-quiet desktop:mx-auto"
                        >
                            {trans('tour.dates.snowtrex.accommodations_count', {
                                '%count%': period.accommodations.length,
                            })}
                        </span>

                        <span class="ml-auto flex items-center gap-xs">
                            <span class="flex flex-col items-end">
                                {#if cheapest.rebateAmount && cheapest.originalPrice}
                                    <span
                                        class="text-xs text-content-quiet line-through"
                                    >
                                        {formatCurrency(
                                            cheapest.originalPrice,
                                            false,
                                        )}
                                    </span>
                                {/if}
                                <span
                                    class={cn(
                                        'font-semibold',
                                        cheapest.rebateAmount
                                            ? 'text-status-negative'
                                            : 'text-content-neutral',
                                    )}
                                >
                                    {trans('tour.dates.price_from')}
                                    {formatCurrency(cheapest.price, false)}
                                </span>
                            </span>
                            <VpChevronDownIcon
                                class={cn(
                                    'size-m shrink-0 transition-transform',
                                    $isSelected(period.id) && 'rotate-180',
                                )}
                            />
                        </span>
                    </button>
                    {#if $isSelected(period.id)}
                        <div
                            {...$content(period.id)}
                            use:content
                            transition:slide
                            class="flex max-h-screen flex-col gap-s overflow-y-auto px-m pb-m"
                        >
                            {#each period.accommodations as accommodation (accommodation.id)}
                                {@const accommodationPrice =
                                    getAccommodationPrice(
                                        accommodation,
                                        $snowtrexSelectedPax,
                                    )}
                                <button
                                    type="button"
                                    class="flex items-center justify-between gap-m rounded-lg border border-solid border-white bg-white p-s text-left hover:border-border-brand hover:bg-extended-cobalt-quiet"
                                    on:click={() =>
                                        selectAccommodation(
                                            period,
                                            accommodation,
                                        )}
                                >
                                    <span class="w-1/2 text-sm md:w-full"
                                        >{accommodation.name}</span
                                    >
                                    <div
                                        class="flex items-center justify-end gap-xs"
                                    >
                                        {#if accommodationPrice.rebateAmount && accommodationPrice.originalPrice}
                                            <span
                                                class="bg-status-negative p-2xs font-semibold text-white"
                                            >
                                                {formatCurrency(
                                                    accommodationPrice.price,
                                                    false,
                                                )}
                                            </span>
                                            <div class="flex flex-col gap-2xs">
                                                <span
                                                    class="text-xs text-content-quiet line-through"
                                                >
                                                    {formatCurrency(
                                                        accommodationPrice.originalPrice,
                                                        false,
                                                    )}
                                                </span>
                                                <span
                                                    class="text-xs text-status-negative"
                                                >
                                                    -{formatCurrency(
                                                        Math.round(
                                                            accommodationPrice.rebateAmount,
                                                        ),
                                                        false,
                                                    )}
                                                </span>
                                            </div>
                                        {:else}
                                            <span
                                                class="bg-container-commercial-catchy p-2xs font-semibold"
                                            >
                                                {formatCurrency(
                                                    accommodationPrice.price,
                                                    false,
                                                )}
                                            </span>
                                        {/if}
                                    </div>
                                    <VpArrowRightIcon
                                        class="size-xl text-content-neutral"
                                    />
                                </button>
                            {/each}
                        </div>
                    {/if}
                </div>
            {/each}
        </div>
    </div>
{:else}
    <div class="flex flex-col gap-xs pt-m text-center">
        <span class="font-semibold">
            {trans('tour.dates.empty.title')}
        </span>
        <span class="text-sm">
            {trans('tour.dates.empty.description')}
        </span>
    </div>
{/if}

<div class="mt-m w-full rounded border border-solid border-border-quiet p-s">
    <div class="flex items-center gap-xs">
        <VpInformationIcon class="size-l text-content-brand" />
        <p class="text-sm">
            {trans('tour.dates.snowtrex.age_reduction_note')}
        </p>
    </div>
</div>
<p class="mt-m text-center text-sm text-content-quiet">
    {trans('tour.dates.snowtrex.booking_instruction')}
</p>
